import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Form, Input, Loading, Switch, Tooltip, Icons } from '@gpstrackit/gtc-react-controls';
import { Card, CardContent, CardHeader } from '@mui/material';
import { NotificationManager } from 'Components/Notifications';
import { updateEscalationConfig, resetEscalation } from './redux/slice';
import './style.scss';

const EscalationWidget = ({ configData }) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const formRef = Form.useForm();
    
    const { user } = useSelector(state => state.securityRedux || {});
    const { update, loading } = useSelector(state => state.escalationRedux || {});
    
    const [data, setData] = useState({ isEnabled: false });
    const [newStepLabel, setNewStepLabel] = useState('');
    const [steps, setSteps] = useState([]);
    const [isEditable, setIsEditable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [labelError, setLabelError] = useState('');

    // Initialize from configData prop
    useEffect(() => {
        if (configData) {
            setData({
                isEnabled: configData.configEnable === 1
            });
            
            // Parse escalationSteps from jsonValues
            if (configData.jsonValues) {
                try {
                    const config = JSON.parse(configData.jsonValues);
                    setSteps(config.escalationSteps || []);
                } catch (error) {
                    console.error('Error parsing escalation config:', error);
                    setSteps([]);
                }
            }
        }
    }, [configData]);

    const validateFields = () => {
        if (!data?.isEnabled) return true;
        return steps.length > 0;
    };

    const handleSave = async () => {
        if (!validateFields()) {
            NotificationManager.error(
                messages['error'],
                messages['driverPerformanceFunctionTab_escalation_escalationStepRequired'],
                3000
            );
            return;
        }

        const payload = {
            config: {
                isEnabled: data?.isEnabled || false,
                escalationSteps: steps
            }
        };
        
        setIsSubmitting(true);
        try {
            await dispatch(updateEscalationConfig(payload));
        } finally {
            setIsSubmitting(false);
        }
    };

    const addStep = () => {
        if (newStepLabel.trim() && newStepLabel.trim().length >= 4 && newStepLabel.trim().length <= 20 && steps.length < 5) {
            const newStep = {
                name: newStepLabel.trim(),
                order: steps.length + 1
            };
            setSteps([...steps, newStep]);
            setNewStepLabel('');
            setLabelError('');
            setIsEditable(true);
        } else if (newStepLabel.trim() && (newStepLabel.trim().length < 4 || newStepLabel.trim().length > 20)) {
            setLabelError(messages['driverPerformanceFunctionTab_escalation_labelLengthError'] || 'Label must be between 4 and 20 characters');
        }
    };

    const removeStep = (index) => {
        const newSteps = steps.filter((_, i) => i !== index)
            .map((step, i) => ({ ...step, order: i + 1 }));
        setSteps(newSteps);
        setIsEditable(true);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const newSteps = [...steps];
        const draggedStep = newSteps[draggedIndex];
        newSteps.splice(draggedIndex, 1);
        newSteps.splice(dropIndex, 0, draggedStep);
        
        // Update order numbers
        const reorderedSteps = newSteps.map((step, i) => ({ ...step, order: i + 1 }));
        setSteps(reorderedSteps);
        setDraggedIndex(null);
        setIsEditable(true);
    };

    const handleToggleChange = (value) => {
        setData({ ...data, isEnabled: value });
        setIsEditable(true);
        
        // If disabling and there are no steps, allow it
        // If enabling, validation will happen on save
        if (!value) {
            // When disabling, we can clear the validation error
        }
    };



    useEffect(() => {
        if (update) {
            if (update?.success) {
                NotificationManager.success(
                    messages['success'],
                    messages['driverPerformanceFunctionTab_escalation_updatedConfiguration'],
                    3000
                );
                setIsEditable(false);
            } else {
                const errorMessage = update?.error || messages['driverPerformanceFunctionTab_escalation_updatedConfigurationError'];
                NotificationManager.error(
                    messages['error'],
                    errorMessage,
                    5000
                );
            }
            dispatch(resetEscalation({ update: null }));
        }
    }, [update, messages, dispatch]);

    return (
        <Card className="widget-paper" style={{ position: 'relative' }}>
            {(loading || isSubmitting) && <Loading overlay show style={{ position: 'absolute' }} />}
            <CardHeader 
                title={
                    <div className="es-header-content">
                        <div className="header-title">
                            {messages['driverPerformanceFunctionTab_escalation_escalation']}
                            <Tooltip
                                className="function-panel-tooltip"
                                placement="bottom"
                                title={messages['driverPerformanceFunctionTab_escalation_labelLengthTooltip'] || 'Length: 4-20 characters'}>
                                <Icons name="help" size="small"/>
                            </Tooltip>
                        </div>
                        <div className="header-row">
                            <Switch
                                checked={data?.isEnabled || false}
                                label={messages['driverPerformanceFunctionTab_escalation_enableEscalation']}
                                labelPosition="left"
                                onChange={handleToggleChange}
                                size="small"
                            />
                        </div>
                    </div>
                }
            />
            <CardContent>
                <Form
                    ref={formRef.ref}
                    loading={loading || isSubmitting}
                    onValidSubmit={async () => {
                        await handleSave();
                    }}
                    className="escalation_widget"
                >
                    <div className="add-step-section">
                        <div className="add-step-input">
                            <Input
                                value={newStepLabel}
                                onChange={(value) => {
                                    setNewStepLabel(value);
                                    setLabelError('');
                                }}
                                placeholder={messages['driverPerformanceFunctionTab_escalation_labelTheValue']}
                                isDisabled={!data?.isEnabled || steps.length >= 5}
                                className="step-input"
                                minLength={4}
                                maxLength={20}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addStep();
                                    }
                                }}
                            />
                            <Button
                                variant="secondary"
                                onClick={addStep}
                                isDisabled={!data?.isEnabled || !newStepLabel.trim() || steps.length >= 5}
                                className="add-button"
                            >
                                {messages['driverPerformanceFunctionTab_escalation_addStep']}
                            </Button>
                        </div>
                        {labelError && (
                            <div className="label-error">
                                {labelError}
                            </div>
                        )}
                    </div>

                    <div className="steps-list">
                        {steps?.length === 0 && (
                            <div className="empty-steps-message">
                                {messages['driverPerformanceFunctionTab_escalation_noStepsAdded']}
                            </div>
                        )}
                        {steps.map((step, index) => (
                            <div 
                                key={`${step.name}-${step.order}`} 
                                className="step-item"
                                draggable={data?.isEnabled}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, index)}
                            >
                                <div className="drag-handle">
                                    <span>::</span>
                                </div>
                                <div className="step-content">
                                    <span className="step-number">{messages['driverPerformanceFunctionTab_escalation_step']} {index + 1}:</span>
                                    <span className="step-label">{step.name}</span>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => removeStep(index)}
                                    isDisabled={!data?.isEnabled}
                                    className="remove-btn"
                                >
                                    ×
                                </Button>
                            </div>
                        ))}
                    </div>

                    {steps.length >= 5 && (
                        <div className="max-steps-notice">
                            {messages['driverPerformanceFunctionTab_escalation_maximumStepsReached']}
                        </div>
                    )}

                    {data?.isEnabled && steps.length === 0 && (
                        <div className="validation-notice">
                            {messages['driverPerformanceFunctionTab_escalation_escalationStepRequired']}
                        </div>
                    )}

                    <div className="widget-footer">
                        <Button
                            variant="primary"
                            onClick={() => formRef?.submit()}
                            isDisabled={!isEditable || (data?.isEnabled && steps.length === 0)}
                            className="save-button"
                        >
                            {messages['save']}
                        </Button>
                    </div>
                </Form>
            </CardContent>
        </Card>
    );
};

export default EscalationWidget;